import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { MapPin, RefreshCw, Save, X, AlertTriangle } from 'lucide-react';
import MainService from '../../services/MainService';

const emptyForm = {
  name: '',
  nameAr: '',
  emirateId: '',
  areaId: '',
  locationUrl: '',
  lat: '',
  lng: '',
};

/**
 * Quick-add location modal for Order Entry.
 */
const AddLocationModal = ({
  open,
  onClose,
  onSaved,
  emirates = [],
  defaultEmirateId = '',
}) => {
  const [form, setForm] = useState(emptyForm);
  const [areas, setAreas] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      ...emptyForm,
      emirateId: defaultEmirateId ? String(defaultEmirateId) : '',
    });
    setError('');
    setSaving(false);
  }, [open, defaultEmirateId]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const response = await MainService.AreasInDropDown();
        if (!cancelled) setAreas(Array.isArray(response) ? response : response?.Data || []);
      } catch (err) {
        console.error('AreasInDropDown error:', err);
        if (!cancelled) setAreas([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Enter location name');
      return;
    }
    if (!form.emirateId) {
      setError('Select emirate');
      return;
    }
    if (!form.areaId) {
      setError('Select area');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = {
        Name: form.name.trim(),
        NameAr: form.nameAr.trim(),
        EmirateId: Number(form.emirateId),
        AreaId: Number(form.areaId),
        LocationUrl: form.locationUrl.trim(),
        LAT: `${form.lat || ''}`,
        LNG: `${form.lng || ''}`,
      };
      const resp = await MainService.SaveLocation(payload);
      if (resp === true || resp === 'true' || resp === 'Success') {
        const saved = {
          Id: resp?.Id || 0,
          Name: payload.Name,
          NameAr: payload.NameAr,
          EmirateId: payload.EmirateId,
          AreaId: payload.AreaId,
          AreaName: areas.find((a) => Number(a.Id) === Number(payload.AreaId))?.Name || '',
          LocationUrl: payload.LocationUrl,
          LAT: payload.LAT,
          LNG: payload.LNG,
        };
        onSaved?.(saved);
        onClose?.();
        return;
      }
      if (resp === 'Failed') {
        setError('Failed to save location');
        return;
      }
      setError(typeof resp === 'string' ? resp : 'Failed to save location');
    } catch (err) {
      console.error('SaveLocation error:', err);
      setError('Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/50 p-4">
      <div
        className="w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-[#46955A]" />
            <h3 className="text-lg font-semibold text-gray-900">Add Location</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
            title="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSave} className="space-y-4 px-5 py-4">
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none focus:ring-1 focus:ring-[#46955A]"
                autoFocus
              />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Name (Arabic)</label>
              <input
                type="text"
                value={form.nameAr}
                onChange={(e) => updateField('nameAr', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none focus:ring-1 focus:ring-[#46955A]"
                dir="rtl"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Emirate <span className="text-red-500">*</span>
              </label>
              <select
                value={form.emirateId}
                onChange={(e) => updateField('emirateId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none focus:ring-1 focus:ring-[#46955A]"
              >
                <option value="">Select Emirate</option>
                {emirates.map((e) => (
                  <option key={e.Id} value={e.Id}>
                    {e.Name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Area <span className="text-red-500">*</span>
              </label>
              <select
                value={form.areaId}
                onChange={(e) => updateField('areaId', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none focus:ring-1 focus:ring-[#46955A]"
              >
                <option value="">Select Area</option>
                {areas.map((a) => (
                  <option key={a.Id} value={a.Id}>
                    {a.Name}
                  </option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Location URL</label>
              <input
                type="text"
                value={form.locationUrl}
                onChange={(e) => updateField('locationUrl', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none focus:ring-1 focus:ring-[#46955A]"
                placeholder="https://maps.google.com/..."
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Latitude</label>
              <input
                type="text"
                value={form.lat}
                onChange={(e) => updateField('lat', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none focus:ring-1 focus:ring-[#46955A]"
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Longitude</label>
              <input
                type="text"
                value={form.lng}
                onChange={(e) => updateField('lng', e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#46955A] focus:outline-none focus:ring-1 focus:ring-[#46955A]"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-100 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-lg bg-[#46955A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#3d824e] disabled:opacity-50"
            >
              {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Location
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AddLocationModal;
